import { useState, useEffect } from 'react';
import s from './AnnotationModal.module.scss';

const EMPTY_ANNOTATION = {
    xAxisCoord: '',
    xAxisRelativeAlignment: '',
    yAxisCoord: '',
    yAxisRelativeAlignment: '',
    text: '',
};

const ALIGNMENT_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];

const AnnotationModal = ({ isOpen, annotation, onSave, onClose }) => {
    const [form, setForm] = useState(EMPTY_ANNOTATION);

    useEffect(() => {
        if (isOpen) {
            setForm(annotation || EMPTY_ANNOTATION);
        }
    }, [isOpen, annotation]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.xAxisCoord || !form.text) return;
        onSave(form);
    };

    const isValid = form.xAxisCoord && form.text;

    if (!isOpen) return null;

    return (
        <div className={s.Overlay} onClick={onClose}>
            <div className={s.Modal} onClick={(e) => e.stopPropagation()}>
                <div className={s.Header}>
                    <h3 className={s.Title}>
                        {annotation ? 'Edit Annotation' : 'Add Annotation'}
                    </h3>
                    <button
                        type="button"
                        className={s.CloseButton}
                        onClick={onClose}>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={s.Form}>
                    <div className={s.Field}>
                        <label className={s.Label}>
                            Text <span className={s.Required}>*</span>
                        </label>
                        <input
                            type="text"
                            className={s.Input}
                            value={form.text}
                            onChange={(e) =>
                                handleChange('text', e.target.value)
                            }
                            placeholder="Annotation text"
                        />
                    </div>

                    <div className={s.FieldRow}>
                        <div className={s.Field}>
                            <label className={s.Label}>
                                X-Axis Coord{' '}
                                <span className={s.Required}>*</span>
                            </label>
                            <input
                                type="text"
                                className={s.Input}
                                value={form.xAxisCoord}
                                onChange={(e) =>
                                    handleChange('xAxisCoord', e.target.value)
                                }
                                placeholder="X position"
                            />
                        </div>
                        <div className={s.Field}>
                            <label className={s.Label}>X Alignment</label>
                            <select
                                className={s.Select}
                                value={form.xAxisRelativeAlignment}
                                onChange={(e) =>
                                    handleChange(
                                        'xAxisRelativeAlignment',
                                        e.target.value
                                    )
                                }>
                                {ALIGNMENT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className={s.FieldRow}>
                        <div className={s.Field}>
                            <label className={s.Label}>Y-Axis Coord</label>
                            <input
                                type="text"
                                className={s.Input}
                                value={form.yAxisCoord}
                                onChange={(e) =>
                                    handleChange('yAxisCoord', e.target.value)
                                }
                                placeholder="Y position (optional)"
                            />
                            <span className={s.HelpText}>
                                Uses data value if blank
                            </span>
                        </div>
                        <div className={s.Field}>
                            <label className={s.Label}>Y Alignment</label>
                            <select
                                className={s.Select}
                                value={form.yAxisRelativeAlignment}
                                onChange={(e) =>
                                    handleChange(
                                        'yAxisRelativeAlignment',
                                        e.target.value
                                    )
                                }>
                                {ALIGNMENT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className={s.Actions}>
                        <button
                            type="button"
                            className={s.CancelButton}
                            onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={s.SaveButton}
                            disabled={!isValid}>
                            {annotation ? 'Update' : 'Add'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AnnotationList = ({ annotations = [], onAdd, onEdit, onRemove }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    const handleAdd = () => {
        setEditingIndex(null);
        setIsModalOpen(true);
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setIsModalOpen(true);
    };

    const handleSave = (annotation) => {
        if (editingIndex !== null) {
            onEdit(editingIndex, annotation);
        } else {
            onAdd(annotation);
        }
        setIsModalOpen(false);
        setEditingIndex(null);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setEditingIndex(null);
    };

    return (
        <div className={s.ListRoot}>
            {annotations.length === 0 ? (
                <p className={s.EmptyText}>No annotations</p>
            ) : (
                <div className={s.AnnotationItems}>
                    {annotations.map((ann, index) => (
                        <div key={index} className={s.AnnotationItem}>
                            <div className={s.AnnotationInfo}>
                                <span className={s.AnnotationText}>
                                    {ann.text}
                                </span>
                                <span className={s.AnnotationCoord}>
                                    x: {ann.xAxisCoord}
                                    {ann.yAxisCoord && `, y: ${ann.yAxisCoord}`}
                                </span>
                            </div>
                            <div className={s.AnnotationActions}>
                                <button
                                    type="button"
                                    className={s.IconButton}
                                    onClick={() => handleEdit(index)}
                                    title="Edit">
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className={s.IconButton}
                                    onClick={() => onRemove(index)}
                                    title="Remove">
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <button
                type="button"
                className={s.AddAnnotationButton}
                onClick={handleAdd}>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                </svg>
                Add Annotation
            </button>

            <AnnotationModal
                isOpen={isModalOpen}
                annotation={
                    editingIndex !== null ? annotations[editingIndex] : null
                }
                onSave={handleSave}
                onClose={handleClose}
            />
        </div>
    );
};

export default AnnotationList;
